import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Container, Dropdown, Form, Header, Icon, Input, Message, Popup, Segment, Table } from "semantic-ui-react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import subMonths from "date-fns/subMonths";
import firebase from "../../../app/config/firebase";
import { size } from "airport-codes";

const firestore = firebase.firestore();

const HISTORY_OPTIONS = [
    { key: 6, text: "Last 6 months", value: 6 },
    { key: 12, text: "Last 12 months", value: 12 },
    { key: 18, text: "Last 18 months", value: 18 },
    { key: 24, text: "Last 24 months", value: 24 },
    { key: "All", text: "All time", value: "All" },
];

const STATUS_OPTIONS = [
    { key: "Active", text: "Active", value: "Active" },
    { key: "Archived", text: "Archived", value: "Archived" },
    { key: "All", text: "All", value: "All" },
];

const DEFAULT_LIMIT = 500;
const DEFAULT_BATCH_SIZE = 50;
const DEFAULT_DELAY_MS = 1000;
const FALLBACK_CREATED_AT = new Date(0);

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const resolveDate = (value) => {
    if (!value) return null;
    if (value.toDate) return value.toDate();
    if (value.seconds) return new Date(value.seconds * 1000);
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
};

const resolveTemplateDate = (template) => {
    if (!template) return null;
    return resolveDate(template.createdAt) || resolveDate(template.createDate) || resolveDate(template.updatedAt) || null;
};

export default function PackageTemplatesPage(props) {
    const { currentUserProfile } = useSelector((state) => state.profile);
    console.log("currentUserProfilexxx", currentUserProfile);
    const [users, setUsers] = useState([{ key: "All", text: "All consultants", value: "All" }]);
    const [selectedUser, setSelectedUser] = useState("All");
    const [historyMonths, setHistoryMonths] = useState(6);
    const [statusFilter, setStatusFilter] = useState("Active");
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [selectedIds, setSelectedIds] = useState({});

    const userNameById = useMemo(() => {
        const map = {};
        users.forEach((user) => {
            if (user.value && user.value !== "All") {
                map[user.value] = user.text;
            }
        });
        return map;
    }, [users]);

    const [preselectYears, setPreselectYears] = useState(2);
    const [backfillLimit, setBackfillLimit] = useState(DEFAULT_LIMIT);
    const [backfillBatchSize, setBackfillBatchSize] = useState(DEFAULT_BATCH_SIZE);
    const [backfillDelayMs, setBackfillDelayMs] = useState(DEFAULT_DELAY_MS);
    const [backfillPreview, setBackfillPreview] = useState(null);
    const [backfillRunning, setBackfillRunning] = useState(false);
    const [backfillProgress, setBackfillProgress] = useState(null);
    const stopRef = useRef(false);

    useEffect(() => {
        const unsubscribe = firestore
            .collection("users")
            .where("isConsultant", "==", true)
            .onSnapshot((snapshot) => {
                let list = [{ key: "All", text: "All consultants", value: "All" }];
                snapshot.docs.forEach((doc) => {
                    if (doc.exists) {
                        const data = doc.data();
                        list.push({ key: doc.id, text: data.displayName || data.email || doc.id, value: doc.id });
                    }
                });
                list.sort((a, b) => (a.text > b.text ? 1 : -1));
                setUsers(list);
            });

        return () => unsubscribe();
    }, []);

    const loadTemplates = useCallback(async () => {
        setLoading(true);
        try {
            let query = firestore.collection("package_templates_index");
            if (selectedUser !== "All") {
                query = query.where("userUid", "==", selectedUser);
            }
            if (historyMonths !== "All") {
                const cutoff = subMonths(new Date(), parseInt(historyMonths, 10));
                query = query.where("createdAt", ">=", cutoff);
            }

            const snap = await query.get();
            const list = [];
            snap.docs.forEach((doc) => {
                if (doc.exists) {
                    list.push({ ...doc.data(), id: doc.id });
                }
            });

            list.sort((a, b) => (a.template_name > b.template_name ? 1 : -1));
            setTemplates(list);
            setSelectedIds({});
        } catch (error) {
            console.log("[admin package templates] load failed", error);
        } finally {
            setLoading(false);
        }
    }, [historyMonths, selectedUser]);

    useEffect(() => {
        if (currentUserProfile?.isLoaded && !currentUserProfile?.isManageTemplates) {
            props.history.push("/");
        }
    }, [currentUserProfile, props.history]);

    useEffect(() => {
        if (!currentUserProfile?.isManageTemplates) return;
        if (selectedUser === "All" && currentUserProfile?.id) {
            setSelectedUser(currentUserProfile.id);
            return;
        }
        loadTemplates();
    }, [currentUserProfile, loadTemplates, selectedUser]);

    const visibleTemplates = useMemo(() => {
        return templates.filter((template) => {
            const isArchived = template.isArchived === true;
            if (statusFilter === "Archived") return isArchived;
            if (statusFilter === "Active") return !isArchived;
            return true;
        });
    }, [statusFilter, templates]);

    const selectedCount = useMemo(() => Object.values(selectedIds).filter(Boolean).length, [selectedIds]);

    const toggleSelectAll = (value) => {
        const updated = {};
        visibleTemplates.forEach((template) => {
            updated[template.id] = value;
        });
        setSelectedIds(updated);
    };

    const preselectOlderThanYears = (years) => {
        const cutoff = new Date();
        cutoff.setFullYear(cutoff.getFullYear() - years);
        const updated = {};
        visibleTemplates.forEach((template) => {
            const createdAt = resolveDate(template.createdAt);
            if (createdAt && createdAt < cutoff) {
                updated[template.id] = true;
            }
        });
        setSelectedIds((prev) => ({ ...prev, ...updated }));
    };

    const handleArchive = async (archive) => {
        const ids = Object.keys(selectedIds).filter((id) => selectedIds[id]);
        if (!ids.length) return;

        const batchSize = 200;
        const archivedAt = firebase.firestore.FieldValue.serverTimestamp();
        const archivedBy = currentUserProfile?.id || "";

        try {
            for (let i = 0; i < ids.length; i += batchSize) {
                const batch = firestore.batch();
                const chunk = ids.slice(i, i + batchSize);
                chunk.forEach((id) => {
                    const data = {
                        isArchived: archive,
                        archivedAt: archive ? archivedAt : null,
                        archivedBy: archive ? archivedBy : null,
                    };
                    batch.set(firestore.collection("package_templates").doc(id), data, { merge: true });
                    batch.set(firestore.collection("package_templates_index").doc(id), data, { merge: true });
                });
                await batch.commit();
            }

            await loadTemplates();
        } catch (error) {
            console.log("[admin package templates] archive failed", error);
        }
    };

    const previewBackfill = async () => {
        if (selectedUser === "All") {
            setBackfillPreview({ error: "Select a consultant before previewing backfill." });
            return;
        }

        setBackfillPreview(null);
        setBackfillProgress(null);

        try {
            let query = firestore.collection("package_templates").where("userUid", "==", selectedUser);
            if (backfillLimit && backfillLimit > 0) {
                query = query.limit(backfillLimit);
            }
            const snap = await query.get();

            let missingCreatedAt = 0;
            let needsIndex = 0;

            snap.docs.forEach((doc) => {
                const data = doc.data() || {};
                const createdAt = resolveTemplateDate(data);
                if (!createdAt) {
                    missingCreatedAt += 1;
                }
                if (!data.template_name && !data.package_name) {
                    // still build index entry, but counts as needsIndex for awareness
                    needsIndex += 1;
                }
            });

            setBackfillPreview({
                total: snap.size,
                missingCreatedAt,
                needsIndex,
            });
        } catch (error) {
            setBackfillPreview({ error: error?.message || "Backfill preview failed" });
        }
    };

    const runBackfill = async () => {
        if (selectedUser === "All") {
            setBackfillPreview({ error: "Select a consultant before running backfill." });
            return;
        }

        setBackfillRunning(true);
        setBackfillProgress({ processed: 0, updated: 0, skipped: 0, batches: 0 });
        stopRef.current = false;

        try {
            let query = firestore.collection("package_templates").where("userUid", "==", selectedUser);
            if (backfillLimit && backfillLimit > 0) {
                query = query.limit(backfillLimit);
            }
            const snap = await query.get();
            const docs = snap.docs;

            for (let i = 0; i < docs.length; i += backfillBatchSize) {
                if (stopRef.current) break;

                const batchDocs = docs.slice(i, i + backfillBatchSize);
                const batch = firestore.batch();
                let updatedCount = 0;
                let skippedCount = 0;

                batchDocs.forEach((doc) => {
                    const data = doc.data() || {};
                    const resolvedCreatedAt = resolveTemplateDate(data);
                    const finalCreatedAt = resolvedCreatedAt || FALLBACK_CREATED_AT;
                    const updatedAt = resolveDate(data.updatedAt) || finalCreatedAt;

                    const updates = {};
                    if (!data.createdAt && resolvedCreatedAt) {
                        updates.createdAt = resolvedCreatedAt;
                    }
                    if (!data.updatedAt && resolvedCreatedAt) {
                        updates.updatedAt = resolvedCreatedAt;
                    }

                    if (Object.keys(updates).length > 0) {
                        batch.set(firestore.collection("package_templates").doc(doc.id), updates, { merge: true });
                    }

                    batch.set(
                        firestore.collection("package_templates_index").doc(doc.id),
                        {
                            userUid: data.userUid || selectedUser,
                            displayName: data.displayName || userNameById[data.userUid] || "",
                            template_name: data.template_name || "",
                            package_name: data.package_name || "",
                            createdAt: finalCreatedAt,
                            updatedAt,
                            isArchived: data.isArchived === true,
                            archivedAt: data.archivedAt || null,
                            archivedBy: data.archivedBy || null,
                        },
                        { merge: true }
                    );

                    if (resolvedCreatedAt || Object.keys(updates).length > 0) {
                        updatedCount += 1;
                    } else {
                        skippedCount += 1;
                    }
                });

                await batch.commit();
                setBackfillProgress((prev) => ({
                    processed: (prev?.processed || 0) + batchDocs.length,
                    updated: (prev?.updated || 0) + updatedCount,
                    skipped: (prev?.skipped || 0) + skippedCount,
                    batches: (prev?.batches || 0) + 1,
                }));

                if (backfillDelayMs > 0 && i + backfillBatchSize < docs.length) {
                    await delay(backfillDelayMs);
                }
            }
        } catch (error) {
            console.log("[admin package templates] backfill failed", error);
        } finally {
            setBackfillRunning(false);
        }
    };

    const stopBackfill = () => {
        stopRef.current = true;
    };

    if (currentUserProfile?.isLoaded === true && currentUserProfile?.isEmpty === true) {
        props.history.push("/");
    }

    if (currentUserProfile?.isLoaded === true && !currentUserProfile?.isManageTemplates) {
        return null;
    }

    return (
        <Container>
            <Header as="h2">
                Package Templates{" "}
                <Popup
                    trigger={<Icon name="info circle" style={{ color: "#f68b43", fontSize: "0.75em", marginBottom: "12px" }} />}
                    content="Filter by consultant, history window, and archive status. Use Archive actions to hide old templates. Backfill rebuilds index docs in small batches so older templates appear in lists."
                    position="right center"
                />
            </Header>

            <Segment>
                <Form>
                    <Form.Group widths="equal">
                        <Form.Field>
                            <label>Consultant</label>
                            <Dropdown
                                selection
                                options={users}
                                value={selectedUser}
                                onChange={(event, data) => setSelectedUser(data.value)}
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>
                                History{" "}
                                <Popup
                                    trigger={<Icon name="info circle" color="grey" />}
                                    content="Controls how far back we fetch index docs. All time can be large."
                                    position="top center"
                                />
                            </label>
                            <Dropdown
                                selection
                                options={HISTORY_OPTIONS}
                                value={historyMonths}
                                onChange={(event, data) => setHistoryMonths(data.value)}
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>
                                Status{" "}
                                <Popup
                                    trigger={<Icon name="info circle" color="grey" />}
                                    content="Active hides archived templates. Switch to All to view both."
                                    position="top center"
                                />
                            </label>
                            <Dropdown
                                selection
                                options={STATUS_OPTIONS}
                                value={statusFilter}
                                onChange={(event, data) => setStatusFilter(data.value)}
                            />
                        </Form.Field>
                    </Form.Group>

                    <Button primary onClick={loadTemplates} loading={loading} disabled={loading} style={{ backgroundColor: "#f68b43", color: "white" }}>
                        Refresh
                    </Button>
                    <Button as={Link} to="/admin/landing">
                        Back to Admin
                    </Button>
                </Form>
            </Segment>

            <Segment>
                <Header as="h4">
                    Archive{" "}
                    <Popup
                        trigger={<Icon name="info circle" style={{ color: "#f68b43" }} />}
                        content="Archive hides templates from dropdowns; it does not delete data."
                        position="right center"
                    />
                </Header>
                <Button disabled={!selectedCount} onClick={() => handleArchive(true)}>
                    Archive Selected ({selectedCount})
                </Button>
                <Button disabled={!selectedCount} onClick={() => handleArchive(false)}>
                    Unarchive Selected ({selectedCount})
                </Button>
                <Button onClick={() => toggleSelectAll(true)}>Select All</Button>
                <Button onClick={() => toggleSelectAll(false)}>Clear All</Button>
                <Form>
                    <Form.Group widths="equal">
                        <Form.Field>
                            <label>
                                Preselect older than{" "}
                                <Popup
                                    trigger={<Icon name="info circle" color="grey" />}
                                    content="Pre-checks templates older than the chosen age so you can archive in bulk."
                                    position="top center"
                                />
                            </label>
                            <Dropdown
                                selection
                                options={[
                                    { key: 1, text: "1 year", value: 1 },
                                    { key: 2, text: "2 years", value: 2 },
                                    { key: 3, text: "3 years", value: 3 },
                                    { key: 4, text: "4 years", value: 4 },
                                    { key: 5, text: "5 years", value: 5 },
                                ]}
                                value={preselectYears}
                                onChange={(event, data) => setPreselectYears(data.value)}
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>&nbsp;</label>
                            <Button onClick={() => preselectOlderThanYears(preselectYears)}>
                                Preselect Older
                            </Button>
                        </Form.Field>
                    </Form.Group>
                </Form>
            </Segment>

            <Table celled selectable>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell width={1}></Table.HeaderCell>
                        <Table.HeaderCell>Template</Table.HeaderCell>
                        <Table.HeaderCell>Package</Table.HeaderCell>
                        <Table.HeaderCell>Consultant</Table.HeaderCell>
                        <Table.HeaderCell>Created</Table.HeaderCell>
                        <Table.HeaderCell>Status</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {visibleTemplates.map((template) => {
                        const createdAt = resolveDate(template.createdAt);
                        const isArchived = template.isArchived === true;
                        const displayName = template.displayName || userNameById[template.userUid] || template.userUid || "";
                        return (
                            <Table.Row key={template.id}>
                                <Table.Cell>
                                    <Checkbox
                                        checked={!!selectedIds[template.id]}
                                        onChange={(event, data) =>
                                            setSelectedIds((prev) => ({ ...prev, [template.id]: data.checked }))
                                        }
                                    />
                                </Table.Cell>
                                <Table.Cell>{template.template_name || "(Unnamed)"}</Table.Cell>
                                <Table.Cell>{template.package_name || ""}</Table.Cell>
                                <Table.Cell>{displayName}</Table.Cell>
                                <Table.Cell>{createdAt ? createdAt.toLocaleDateString() : "Missing"}</Table.Cell>
                                <Table.Cell>{isArchived ? "Archived" : "Active"}</Table.Cell>
                            </Table.Row>
                        );
                    })}
                </Table.Body>
            </Table>

            <Segment>
                <Header as="h4">
                    Backfill Index{" "}
                    <Popup
                        trigger={<Icon name="info circle" style={{ color: "#f68b43" }} />}
                        content="Backfill rebuilds index docs in controlled batches. It is safe and does not delete templates."
                        position="right center"
                    />
                </Header>
                <Message info>
                    <Message.Header>Use this to rebuild missing index entries and createdAt fields.</Message.Header>
                    <p>
                        Select a consultant, preview counts, then run in small batches. Missing dates fall back to Jan 1, 1970
                        in the index so they still appear under All time.
                    </p>
                </Message>

                <Form>
                    <Form.Group widths="equal">
                        <Form.Field>
                            <label>Limit</label>
                            <Input
                                type="number"
                                value={backfillLimit}
                                onChange={(event) => setBackfillLimit(parseInt(event.target.value || "0", 10))}
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>Batch Size</label>
                            <Input
                                type="number"
                                value={backfillBatchSize}
                                onChange={(event) => setBackfillBatchSize(parseInt(event.target.value || "0", 10))}
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>Delay (ms)</label>
                            <Input
                                type="number"
                                value={backfillDelayMs}
                                onChange={(event) => setBackfillDelayMs(parseInt(event.target.value || "0", 10))}
                            />
                        </Form.Field>
                    </Form.Group>

                    <Button onClick={previewBackfill} disabled={backfillRunning} >
                        Preview
                    </Button>
                    <Button primary onClick={runBackfill} loading={backfillRunning} disabled={backfillRunning} style={{ backgroundColor: "#f68b43", color: "white" }}>
                        Run Backfill
                    </Button>
                    <Button onClick={stopBackfill} disabled={!backfillRunning}>
                        Stop
                    </Button>
                </Form>

                {backfillPreview?.error && (
                    <Message negative>
                        <Message.Header>Backfill Preview Failed</Message.Header>
                        <p>{backfillPreview.error}</p>
                    </Message>
                )}

                {backfillPreview && !backfillPreview.error && (
                    <Message>
                        <Message.Header>Preview Results</Message.Header>
                        <p>Total scanned: {backfillPreview.total}</p>
                        <p>Missing createdAt: {backfillPreview.missingCreatedAt}</p>
                        <p>Potential index gaps: {backfillPreview.needsIndex}</p>
                    </Message>
                )}

                {backfillProgress && (
                    <Message>
                        <Message.Header>Backfill Progress</Message.Header>
                        <p>Processed: {backfillProgress.processed}</p>
                        <p>Updated: {backfillProgress.updated}</p>
                        <p>Skipped: {backfillProgress.skipped}</p>
                        <p>Batches: {backfillProgress.batches}</p>
                    </Message>
                )}
            </Segment>
        </Container>
    );
}






